import {
    useState,
    useEffect,
    useMemo,
    Dispatch,
    SetStateAction,
    useContext,
} from "react"
import { DbEntry } from "./types"
import AuthContext from "../context/AuthContext"
import { useHistory, useRouteMatch } from "react-router-dom"
import { History } from "history"
import { useCartUpdater } from "../context/CartContext"

export function useLocalStorage<T>(
    key: string,
    initialValue: T
): [T, (value: T) => void] {
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState<T>(() => {
        try {
            // Get from local storage by key
            const item = window.localStorage.getItem(key)
            // Parse stored json or if none return initialValue
            return item ? JSON.parse(item) : initialValue
        } catch (error) {
            // If error also return initialValue
            console.log(error)
            return initialValue
        }
    })

    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to localStorage.
    const setValue = (value: T) => {
        try {
            // Allow value to be a function so we have same API as useState
            const valueToStore =
                value instanceof Function ? value(storedValue) : value
            // Save state
            setStoredValue(valueToStore)
            // Save to local storage
            window.localStorage.setItem(key, JSON.stringify(valueToStore))
        } catch (error) {
            // A more advanced implementation would handle the error case
            console.log(error)
        }
    }

    return [storedValue, setValue]
}

export function useDebounce<T>(value: T, delay: number = 500) {
    const [debouncedValue, setDebouncedValue] = useState(value)

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value)
        }, delay)

        return () => {
            clearTimeout(handler)
        }
    }, [value, delay])

    return debouncedValue
}

export function useSearchTextOnName<T>(
    itemsToFilter: Array<
        DbEntry<T> & { data?: { name: string; itemNumber?: string } }
    >
): [DbEntry<T>[], string, Dispatch<SetStateAction<string>>] {
    const [searchText, setSearchText] = useState("")

    const debouncedSearchText = useDebounce<string>(searchText, 0)

    const filteredItems = useMemo(() => {
        if (debouncedSearchText !== "") {
            const searchTextPieces = debouncedSearchText.split(" ")
            return itemsToFilter.filter((item) =>
                searchTextPieces.every(
                    (piece) =>
                        item.data?.name
                            .toUpperCase()
                            .indexOf(piece.toUpperCase()) !== -1 ||
                        (item.data?.itemNumber &&
                            item.data.itemNumber
                                .toUpperCase()
                                .indexOf(piece.toUpperCase()) !== -1)
                )
            )
        }
        return itemsToFilter
    }, [itemsToFilter, debouncedSearchText])

    return [filteredItems, searchText, setSearchText]
}

export function useSearchTextOnSnapshot<T = Array<string | null>>(
    property: number,
    itemsToFilter: T[] = []
): [T[], string, Dispatch<SetStateAction<string>>] {
    const [searchText, setSearchText] = useState("")

    const debouncedSearchText = useDebounce<string>(searchText, 0)

    const filteredProducts = useMemo(() => {
        if (debouncedSearchText !== "") {
            return itemsToFilter.filter(
                (item) =>
                    //@ts-ignore
                    item[property] !== null && // TODO can I do something with this typing?
                    //@ts-ignore
                    item[property]
                        .toUpperCase()
                        .indexOf(debouncedSearchText.toUpperCase()) !== -1
            )
        }
        return itemsToFilter
    }, [itemsToFilter, debouncedSearchText, property])

    return [filteredProducts, searchText, setSearchText]
}

export function useLoginLogout(): {
    label: "Bejelentkezés" | "Kijelentkezés"
    handler: () => void
    isAuthenticated: boolean
} {
    const auth = useContext(AuthContext)
    const isAuthenticated = auth?.isAuthenticated()
    const { clearCart } = useCartUpdater()

    const handleLogout = () => {
        clearCart()
        auth?.logout()
    }

    const handleLogin = () => {
        auth?.login()
    }

    return {
        label: isAuthenticated ? "Kijelentkezés" : "Bejelentkezés",
        handler: isAuthenticated ? handleLogout : handleLogin,
        isAuthenticated: !!isAuthenticated,
    }
}

export function useNavigateToCreated(): {
    navigateTo: (createdElementId: string) => void
    history: History
} {
    const history = useHistory()
    const { url } = useRouteMatch()

    const navigateTo = (createdElementId: string) => {
        const newPath = url.replace("new", createdElementId)
        history.push(newPath)
    }

    return {
        history,
        navigateTo,
    }
}
